import type { RestorableSwap } from "boltz-swaps/client";
import {
    type Accessor,
    type JSX,
    type Setter,
    createContext,
    createSignal,
    useContext,
} from "solid-js";

import type { AssetType } from "../consts/Assets";
import type { EvmRescueResult } from "../pages/external-rescue/types";
import { type ECKeys, ECPair } from "../utils/ecpair";
import { type RescueFile, deriveKey } from "../utils/rescueFile";

export const mnemonicLength = 12;
export const rescueKeyMode = "rescue-key";

export type RescueContextType = {
    rescueFile: Accessor<RescueFile | undefined>;
    setRescueFile: Setter<RescueFile | undefined>;

    rescuableSwaps: Accessor<RestorableSwap[]>;
    setRescuableSwaps: Setter<RestorableSwap[]>;

    evmRescuableSwaps: Accessor<EvmRescueResult[]>;
    setEvmRescuableSwaps: Setter<EvmRescueResult[]>;

    validRescueKey: Accessor<boolean>;
    setValidRescueKey: Setter<boolean>;

    resetRescueKey: () => void;

    deriveKey: (index: number, asset: AssetType) => ECKeys;
};

const RescueContext = createContext<RescueContextType>();

export const RescueProvider = (props: { children: JSX.Element }) => {
    const [rescueFile, setRescueFile] = createSignal<RescueFile>();
    const [rescuableSwaps, setRescuableSwaps] = createSignal<RestorableSwap[]>(
        [],
    );
    const [evmRescuableSwaps, setEvmRescuableSwaps] = createSignal<
        EvmRescueResult[]
    >([]);
    const [validRescueKey, setValidRescueKey] = createSignal<boolean>(false);

    const resetRescueKey = () => {
        setValidRescueKey(false);
        setRescueFile(undefined);
    };

    const deriveKeyWrapper = (index: number, asset: AssetType) => {
        const rf = rescueFile();
        if (rf === undefined) {
            throw new Error("rescue file is not initialised");
        }
        const derived = deriveKey(rf, index, asset);
        if (derived.privateKey === null) {
            throw new Error("derived private key is null");
        }
        return ECPair.fromPrivateKey(new Uint8Array(derived.privateKey));
    };

    return (
        <RescueContext.Provider
            value={{
                rescueFile,
                setRescueFile,
                rescuableSwaps,
                setRescuableSwaps,
                evmRescuableSwaps,
                setEvmRescuableSwaps,
                validRescueKey,
                setValidRescueKey,
                resetRescueKey,
                deriveKey: deriveKeyWrapper,
            }}>
            {props.children}
        </RescueContext.Provider>
    );
};

export const useRescueContext = () => {
    const context = useContext(RescueContext);
    if (!context) {
        throw new Error("useRescueContext: cannot find a RescueContext");
    }
    return context;
};
